import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom'; // Asegúrate de usar react-router-dom
import IDHelpers from '../../../../../core/helpers/IDHelper';
import TabContent from './TabContent';
import {
  ContentWrapper,
  Container,
  Button,
  Titles
} from '../../../common/TabsStyled';
import { useWindowSize } from '@/core/hooks/useWindowSize';
import { DateNavigator } from '@/presentation/reusables/DateNavigator';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import { CATEGORIES } from '../../constants';

interface EventsBodyViewProps {
  categories: string[];
  handleTab: (selected: number) => void;
  selectEvent: (eventId: number, typeEvent: string) => void;
  scrollContainer: any;
}

const EventsBodyView = (props: EventsBodyViewProps) => {
  const { width } = useWindowSize();
  const containerRef = useRef(null);
  const [width1, setWidth1] = useState(0);
  const history = useHistory();

  // Obtén el parámetro de la URL (asegúrate de que el nombre coincide con el definido en tu ruta)
  const { category } = useParams<{ category: string }>();

  // Mapeo para asignar el valor correcto
  const mapping: { [key: string]: number } = {
    inscritos: 1,
    talleres: 2,
    conferencias: 3,
    actividades: 4
  };

  // Estado inicial basado en el parámetro de la URL
  const initialToggleState = category
    ? mapping[category.toLowerCase()] || 1
    : 1;
  const [courseIndex, setCourseIndex] = useState(initialToggleState);
  const [toggleState, setToggleState] = useState(initialToggleState);
  const [counterSubscriptions, setCounterSubscriptions] = useState<number>(
    () => Number(sessionStorage.getItem('counterSubscriptions')) || 0
  );
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showLeft, setShowLeft] = useState(false);

  const { categories, handleTab, selectEvent, scrollContainer } = props;

  const { isLoading } = useSelector(
    (state: any) => state?.getEventsByStudentScroll
  );

  const widthToDiv = (): number => {
    const contenedor = scrollContainer?.current;
    let valueWidth = 0;
    if (contenedor) {
      const hijos = contenedor.children;
      for (let i = 0; i < hijos.length; i++) {
        valueWidth += Number(hijos[i].getBoundingClientRect().width);
      }
    }
    setWidth1(valueWidth);
    return valueWidth || 0;
  };

  const validateArrowRight = () => {
    if (width1 === 0) return null;
    if (width1 > width) {
      setShowLeftArrow(true);
      return true;
    }
    setShowLeftArrow(false);
    return false;
  };

  const validateArrowLeft = () => {
    if (widthToDiv() > scrollContainer?.current?.clientWidth) {
      if (courseIndex === 1) {
        setShowLeft(false);
      }
      if (courseIndex !== 1) {
        setShowLeft(true);
      }
    } else {
      setShowLeft(false);
    }
  };

  useEffect(() => {
    const validate = validateArrowRight();
    if (validate) {
      if (categories.length === courseIndex) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(validate);
      }
    } else if (width && widthToDiv() < width) {
      setShowLeftArrow(false);
    }
    validateArrowLeft();
  }, [width, width1]);

  const updatePosition = () => {
    if (scrollContainer?.current) {
      const activeElement = scrollContainer?.current.querySelector('.active');
      if (activeElement && scrollContainer?.current?.scrollTo) {
        scrollContainer?.current?.scrollTo({
          left:
            activeElement.offsetLeft -
            (scrollContainer.current.offsetWidth - activeElement.offsetWidth) /
              2,
          behavior: 'smooth'
        });
      }
    }
  };

  const googleAnalyticsEvents = (index: string) => {
    // eslint-disable-next-line default-case
    switch (index) {
      case '1':
        GoogleAnalytics.writeLog(
          'portal-services-eventsInscriptions',
          'services'
        );
        break;
      case '2':
        GoogleAnalytics.writeLog('portal-services-eventsWorkshops', 'services');
        break;
      case '3':
        GoogleAnalytics.writeLog(
          'portal-services-eventsConferences',
          'services'
        );
        break;
      case '4':
        GoogleAnalytics.writeLog('portal-services-eventsActivies', 'services');
        break;
    }
  };

  const toggleTab = (index: string) => {
    googleAnalyticsEvents(index);
    history.push(`/servicios/eventos/${CATEGORIES[Number(index) - 1]}`);
    setToggleState(Number(index));
    setCourseIndex(Number(index));
    validateArrowLeft();
  };

  // Ejecuta toggleTab si el parámetro de la URL cambia y difiere del estado actual
  useEffect(() => {
    if (category) {
      const index = mapping[category.toLowerCase()];
      if (index && index !== toggleState) {
        toggleTab(index.toString());
      }
    }
  }, [category]);

  useEffect(() => {
    setToggleState(courseIndex);
    validateArrowLeft();
  }, [courseIndex]);

  useEffect(() => {
    const savedCount =
      Number(sessionStorage.getItem('counterSubscriptions')) || 0;
    setCounterSubscriptions(savedCount);
  }, []);

  useEffect(() => {
    if (courseIndex === categories.length) {
      setShowLeftArrow(false);
    } else {
      validateArrowRight();
    }
    validateArrowLeft();
  }, [courseIndex]);

  useEffect(() => {
    updatePosition();
  }, [courseIndex, width]);

  return (
    <Container data-testid="cmp-events-body-view">
      <Titles
        className="no-scroll"
        ref={containerRef}
        data-testid="container-ref"
      >
        {showLeft && (
          <DateNavigator
            leftDate
            left="16px"
            tabs={categories}
            courseIndex={courseIndex}
            selectIndex={setCourseIndex}
            toggleTab={toggleTab}
          />
        )}
        <div ref={scrollContainer} style={{ display: 'flex' }}>
          {categories.map((categoryItem: string, index: number) => {
            const order = index + 1;
            return (
              <div
                data-testid="child"
                key={IDHelpers.generateID()}
                className={toggleState === order ? 'active' : ''}
              >
                <Button
                  data-testid="cmp-button-activetab"
                  className={`tabs ${toggleState === order && 'active-tabs'}`}
                  onClick={() => toggleTab(order.toString())}
                  disabled={isLoading}
                >
                  {categoryItem}{' '}
                  {categoryItem === 'Inscritos' && (
                    <>
                      {isLoading ? (
                        <span>(0)</span>
                      ) : (
                        <span>({counterSubscriptions})</span>
                      )}
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
        {showLeftArrow && (
          <DateNavigator
            leftDate={false}
            right="16px"
            tabs={categories}
            courseIndex={courseIndex}
            selectIndex={setCourseIndex}
            toggleTab={toggleTab}
          />
        )}
      </Titles>
      <ContentWrapper>
        <TabContent
          current={toggleState}
          handleTab={handleTab}
          selectEvent={selectEvent}
          setCounterSubscriptions={setCounterSubscriptions}
        />
      </ContentWrapper>
    </Container>
  );
};

export default EventsBodyView;
